// Outer
import React from 'react';
import {
  TableRow,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableFooter,
  TablePagination,
  Box,
  IconButton,
} from '@mui/material';
import {format} from 'date-fns';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {library} from '@fortawesome/fontawesome-svg-core';
import {fas} from '@fortawesome/free-solid-svg-icons';
import {far} from '@fortawesome/free-regular-svg-icons';
import {FaSortAmountDownAlt, FaSortAmountDown} from 'react-icons/fa';

import {
  MdSkipPrevious,
  MdNavigateBefore,
  MdNavigateNext,
  MdSkipNext,
} from 'react-icons/md';
import {PAGE_SIZE, tablePageSizes} from '../config';
import {useSanitizeHtml} from '@/common/hooks/useSanitizeHtml';
import {resolveRightsholderImageUrl} from '@/common/config';

library.add(fas, far);

const LINES_TO_SHOW = 5;

function TablePaginationActions(props: any) {
  const {count, page, rowsPerPage, onPageChange} = props;
  const pageCount = Math.ceil(count / rowsPerPage) - 1;

  const handleFirstPageButtonClick = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    onPageChange(event, 0);
  };

  const handleBackButtonClick = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    onPageChange(event, page - 1);
  };

  const handleNextButtonClick = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    onPageChange(event, page + 1);
  };

  const handleLastPageButtonClick = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    onPageChange(event, Math.max(0, pageCount));
  };

  return (
    <Box display="flex">
      <IconButton
        onClick={handleFirstPageButtonClick}
        disabled={page === 0}
        aria-label="first page"
      >
        <MdSkipPrevious />
      </IconButton>
      <IconButton
        onClick={handleBackButtonClick}
        disabled={page === 0}
        aria-label="previous page"
      >
        <MdNavigateBefore />
      </IconButton>
      <IconButton
        onClick={handleNextButtonClick}
        disabled={page >= pageCount}
        aria-label="next page"
      >
        <MdNavigateNext />
      </IconButton>
      <IconButton
        onClick={handleLastPageButtonClick}
        disabled={page >= pageCount}
        aria-label="last page"
      >
        <MdSkipNext />
      </IconButton>
    </Box>
  );
}

export default function SimpleTable({
  columns,
  rows,
  onSelect,
  isPagination,
  totalCount,
  pageSize,
  setPageSize,
  pageNumber,
  setPage,
  style,
  isSortable,
  handleClickTitle,
  filters,
}: any) {
  const handleChangePageSize = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const newPageSize = parseInt(event.target.value, 10);
    setPageSize(newPageSize as PAGE_SIZE);
  };

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    setPage(newPage);
  };

  return (
    <>
      <Table style={style}>
        <TableHead>
          <TableRow
            sx={{
              '& th': {
                verticalAlign: 'top',
                padding: '5px 10px', // Change indents globally
              },
            }}
          >
            {columns.map((column: any) => {
              if (column.invisible) return null;
              const accessor = column.accessor;
              const stylesTitleHolder = `title-holder ${filters?.sortBy === accessor ? 'active' : null}`;

              return (
                <TableCell
                  key={column.name}
                  onClick={() => {
                    if (!isSortable) return;

                    handleClickTitle(accessor);
                  }}
                  sx={
                    isSortable
                      ? {
                          cursor: 'pointer',
                          alignItems: 'center',
                          '&:hover': {
                            textDecoration: 'underline',
                          },
                          '& .icon': {
                            display: 'inline-block',
                            padding: '0 0 0 3px',
                            position: 'relative',
                            top: '3px',
                            opacity: '0.3',
                          },
                          '& .title-holder': {
                            display: 'flex',
                            alignItems: 'center',
                          },
                          '& .title-holder.active': {
                            textDecoration: 'underline',
                            '& .icon': {
                              opacity: 1,
                            },
                          },
                        }
                      : null
                  }
                >
                  <div className={stylesTitleHolder}>
                    {column.name}
                    {isSortable && (
                      <div className="icon">
                        {filters.sortBy === accessor && !filters.orderAsc ? (
                          <FaSortAmountDown size={15} color={'white'} />
                        ) : (
                          <FaSortAmountDownAlt size={15} color={'white'} />
                        )}
                      </div>
                    )}
                  </div>
                </TableCell>
              );
            })}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row: any) => {
            const styleTableRow: any = {};
            const isActive = row.active !== false;

            if (!isActive) {
              styleTableRow['opacity'] = 0.2;
              styleTableRow['background'] = 'black';
            }

            return (
              <TableRow
                hover={isActive}
                key={row.id}
                selected={false}
                sx={{
                  cursor: 'pointer',
                  '& p': {
                    marginTop: 0,
                  },
                  '& th': {
                    verticalAlign: 'top',
                    padding: '5px 10px', // Change indents globally
                  },
                }}
                style={styleTableRow}
                onClick={() => {
                  onSelect(row.id);
                }}
              >
                {columns.map((cell: any) => {
                  const accessor = cell.accessor;
                  const style = cell.style || {};
                  const content = row[accessor];
                  const dateRegex =
                    /^(?:\d{4})-(?:\d{2})-(?:\d{2})T(?:\d{2}):(?:\d{2}):(?:\d{2}(?:\.\d*)?)(?:(?:-(?:\d{2}):(?:\d{2})|Z)?)$/;
                  let currentContent;
                  const arrCleanHtml = ['description'];
                  const arrSum = ['advanceamount', 'amount', 'balance'];
                  const arrImg = ['coverId', 'imageId'];

                  if (cell.invisible) {
                    return null;
                  } else if (dateRegex.test(content)) {
                    currentContent = format(new Date(content), 'MM/dd/yyyy');
                  } else if (arrCleanHtml.includes(accessor)) {
                    currentContent = (
                      <div
                        className="clear-indents"
                        // eslint-disable-next-line react-hooks/rules-of-hooks
                        dangerouslySetInnerHTML={{
                          __html: useSanitizeHtml(content),
                        }}
                      />
                    );
                  } else if (arrSum.includes(accessor)) {
                    currentContent = `$${content.toLocaleString()}`;
                  } else if (
                    accessor === 'paymentType' &&
                    content === 'Deleted'
                  ) {
                    style['color'] = '#d32f2f';
                    currentContent = String(content);
                  } else if (arrImg.includes(accessor)) {
                    let value = content;
                    if (typeof content === 'number') {
                      value = resolveRightsholderImageUrl(content);
                    }
                    currentContent = (
                      <img
                        src={
                          value
                            ? value
                            : 'https://placehold.co/60x90?text=Not+Found'
                        }
                        width={90}
                        alt={accessor}
                      />
                    );
                  } else if (accessor === 'iconName') {
                    currentContent = (
                      <FontAwesomeIcon size="1x" color="#fff" icon={content} />
                    );
                  } else {
                    currentContent = String(content);
                  }

                  return (
                    <TableCell
                      key={accessor}
                      component="th"
                      scope="row"
                      style={{...style}}
                    >
                      <Box
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: LINES_TO_SHOW,
                          WebkitBoxOrient: 'vertical',
                        }}
                      >
                        {currentContent}
                      </Box>
                    </TableCell>
                  );
                })}
              </TableRow>
            );
          })}
        </TableBody>
        {isPagination && (
          <TableFooter>
            <TableRow>
              {/* @ts-ignore */}
              <TablePagination
                colSpan={4}
                rowsPerPageOptions={[...tablePageSizes]}
                count={totalCount}
                rowsPerPage={pageSize}
                page={pageNumber}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangePageSize}
                ActionsComponent={TablePaginationActions}
              />
            </TableRow>
          </TableFooter>
        )}
      </Table>
    </>
  );
}
